import React, { useState, useEffect } from "react";
import { FaChevronRight, FaCheck, FaUsers, FaChartBar, FaCog, FaShieldAlt, FaArrowRight, FaSpinner, FaBolt, FaEye, FaRocket, FaStar, FaLinkedin, FaTwitter, FaFacebook } from "react-icons/fa";
import logo from "../assets/ezOCM_Logo.png";
import config from "../config";

const customerLogos = [
  { name: "Microsoft", logo: "https://img.logos-world.net/wp-content/uploads/2020/09/Microsoft-Logo.png" },
  { name: "Amazon", logo: "https://logoeps.com/wp-content/uploads/2013/03/amazon-vector-logo.png" },
  { name: "Google", logo: "https://logos-world.net/wp-content/uploads/2020/09/Google-Logo.png" },
  { name: "IBM", logo: "https://logos-world.net/wp-content/uploads/2020/09/IBM-Logo.png" },
  { name: "Oracle", logo: "https://logos-world.net/wp-content/uploads/2020/09/Oracle-Logo.png" },
  { name: "SAP", logo: "https://logos-world.net/wp-content/uploads/2020/09/SAP-Logo.png" }
];

const testimonials = [
  {
    quote: "The Change Management Platform has been a true game changer for our organization in how we understand and manage the impact of change on our people. It provides clear visibility of all ongoing changes and helps our business manage workflow and resources effectively.",
    author: "Sarah Mitchell",
    company: "Global Technology Corp",
    role: "VP of Change Management"
  },
  {
    quote: "This platform has been an invaluable tool that has helped us plan and manage change. The AI-powered insights have been a game changer for our team and organization. With excellent customer support, implementation was seamless.",
    author: "Michael Chen",
    company: "Financial Services Ltd",
    role: "Director of Operations"
  },
  {
    quote: "I was very impressed by the Change Management Platform. I found the experience simple, user-friendly and easy to set up for our business. The reports were meaningful and revealing, allowing for great conversations, insights and decision-making.",
    author: "Jessica Thompson",
    company: "Healthcare Innovations",
    role: "Chief Transformation Officer"
  }
];

export default function Homepage() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Pricing state
  const [pricingPlans, setPricingPlans] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingError, setPricingError] = useState(null);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      alert('Thank you for your message! We\'ll get back to you within 24 hours.');
      setContactForm({ name: '', email: '', company: '', message: '', phone: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  const handleContactChange = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  // Fetch pricing plans on component mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        setPricingLoading(true);
        const response = await fetch(`${config.API_BASE_URL}/public/pricing`);
        const data = await response.json();
        
        if (data.success) {
          setPricingPlans(data.plans);
          setPricingError(null);
        } else {
          setPricingError(data.error || 'Failed to load pricing');
          // Fallback to default plans if API fails
          setPricingPlans([
            {
              name: "Free",
              price: "Free",
              period: "",
              description: "Perfect for individuals and small teams",
              features: ["Up to 3 projects", "Up to 10 stakeholders", "Basic analytics", "Email support"],
              popular: false,
              ctaText: "Start Free"
            }
          ]);
        }
      } catch (error) {
        console.error('Error fetching pricing:', error);
        setPricingError('Failed to load pricing');
        // Fallback to default plans
        setPricingPlans([
          {
            name: "Free",
            price: "Free", 
            period: "",
            description: "Perfect for individuals and small teams",
            features: ["Up to 3 projects", "Up to 10 stakeholders", "Basic analytics", "Email support"],
            popular: false,
            ctaText: "Start Free"
          }
        ]);
      } finally {
        setPricingLoading(false);
      }
    };

    fetchPricing();
  }, []);

  return (
    <div className="min-h-screen flex flex-col font-sans bg-white m-0 p-0">
      <style jsx global>{`
        html, body {
          margin: 0 !important;
          padding: 0 !important;
        }
        
        html {
          scroll-behavior: smooth;
        }
        
        section[id] {
          scroll-margin-top: 80px;
        }

        .gradient-text {
          background: linear-gradient(135deg, #FF6B6B 0%, #FFA07A 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-gradient {
          background: linear-gradient(135deg, #FFF8F3 0%, #FFE4E1 100%);
        }

        .feature-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 25px -5px rgba(255, 107, 107, 0.15), 0 10px 10px -5px rgba(255, 160, 122, 0.08);
        }

        .testimonial-card {
          background: linear-gradient(135deg, #FFFAF5 0%, #FFF8F3 100%);
          border: 1px solid #FFE4E1;
        }

        .demo-section {
          background: linear-gradient(135deg, #FF6B6B 0%, #FFD93D 100%);
        }
      `}</style>

      {/* Header Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-orange-100" style={{margin: '0', padding: '0', position: 'fixed', top: '0', backgroundColor: 'rgba(255, 248, 243, 0.95)'}}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-2">
          {/* Logo */}
          <div className="flex items-center">
            <img src={logo} alt="ezOCM Change Management Platform" className="h-48 w-auto" />
          </div>
          
          {/* Nav Links */}
          <div className="hidden lg:flex gap-8 text-base font-medium text-gray-700">
            <a href="#features" className="hover:text-orange-500 transition-colors duration-200">Features</a>
            <a href="#solutions" className="hover:text-orange-500 transition-colors duration-200">Solutions</a>
            <a href="#pricing" className="hover:text-orange-500 transition-colors duration-200">Pricing</a>
            <a href="#customers" className="hover:text-orange-500 transition-colors duration-200">Customers</a>
            <a href="#contact" className="hover:text-orange-500 transition-colors duration-200">Contact</a>
          </div>
          
          {/* CTAs */}
          <div className="flex gap-3">
            <a href="/signin" className="px-6 py-2 rounded-lg text-gray-700 hover:text-orange-600 transition-colors duration-200 font-medium">
              Sign In</a>
            <a href="/register" className="px-6 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:from-orange-600 hover:to-pink-600 transition-all duration-200 font-medium shadow-lg hover:shadow-xl">
              Get Demo</a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-gradient overflow-hidden" style={{marginTop: '80px', paddingTop: '80px', paddingBottom: '80px'}}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center space-y-8">
            <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight">
              Drive stakeholder action and <br />
              <span className="gradient-text">change outcome through data</span>
            </h1>
            
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="flex flex-col md:flex-row gap-4 justify-center items-center text-lg text-gray-700">
                <div className="flex items-center space-x-3">
                  <FaBolt className="text-orange-500" />
                  <span>Utilise AI to identify and mitigate change overload</span>
                </div>
                <div className="flex items-center space-x-3">
                  <FaShieldAlt className="text-teal-500" />
                  <span>Protect your organisation from millions of losses from performance dips</span>
                </div>
                <div className="flex items-center space-x-3">
                  <FaChartBar className="text-pink-500" />
                  <span>Generate adoption and readiness dashboards quickly and intuitively</span>
                </div>
              </div>
              </div>
              
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact" className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-xl">
                Get a Demo
                <FaArrowRight className="ml-2" /></a>
              <a href="#features" className="inline-flex items-center justify-center px-8 py-4 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold text-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-200">
                Watch Video
                <FaChevronRight className="ml-2" /></a>
            </div>

            <p className="text-sm text-gray-600 max-w-2xl mx-auto">
              Enterprise change management platform trusted by leading organizations worldwide
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Features that drive successful change outcomes
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="feature-card p-8 rounded-2xl bg-white border border-gray-200 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-6">
                <FaChartBar className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Smart charting</h3>
              <p className="text-gray-600">Use ready-made chart layouts to visualise the metrics that matter</p>
            </div>

            <div className="feature-card p-8 rounded-2xl bg-gradient-to-br from-white to-orange-50 border border-orange-100 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-400 flex items-center justify-center mb-6">
                <FaCog className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Digital change delivery</h3>
              <p className="text-gray-600">Let AI do some of the leg work in generating change documents</p>
            </div>

            <div className="feature-card p-8 rounded-2xl bg-gradient-to-br from-white to-orange-50 border border-orange-100 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-yellow-400 flex items-center justify-center mb-6">
                <FaUsers className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Data collection tools</h3>
              <p className="text-gray-600">Use proven templates or surveys with ready-made charts and dashboards</p>
            </div>

            <div className="feature-card p-8 rounded-2xl bg-gradient-to-br from-white to-orange-50 border border-orange-100 transition-all duration-300">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-400 to-pink-400 flex items-center justify-center mb-6">
                <FaBolt className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Automated insights</h3>
              <p className="text-gray-600">Ask data questions and get instant answers with AI</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-20 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Change and transformation teams
            </h2>
            <h3 className="text-3xl lg:text-4xl font-bold gradient-text mb-8">
              use our platform to:
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-white to-orange-50 shadow-lg border border-orange-100">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-coral-400 to-orange-400 flex items-center justify-center mx-auto mb-6">
                <FaRocket className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Save millions in revenue</h3>
              <p className="text-gray-600">through better employee & customer experiences</p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-white to-orange-50 shadow-lg border border-orange-100">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center mx-auto mb-6">
                <FaEye className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Get insights on change capacity</h3>
              <p className="text-gray-600">to de-risk performance disruptions</p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-white to-orange-50 shadow-lg border border-orange-100">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-400 to-rose-400 flex items-center justify-center mx-auto mb-6">
                <FaChartBar className="text-white text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Forecast change impacts</h3>
              <p className="text-gray-600">on people across initiatives and adoption risks</p>
            </div>
              </div>
              
          {/* Product Showcase Sections */}
          <div className="space-y-20">
            {/* Stakeholder Measurement */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h3 className="text-3xl lg:text-4xl font-bold text-gray-900">
                  Measure where your targeted stakeholders are in their change adoption
                </h3>
                <p className="text-lg text-gray-600">
                  Get insights on where your initiative adoption is missing the mark.
                </p>
                <p className="text-gray-600">
                  Zoom in on stakeholder readiness responses or key targeted behaviours. Find out what needs to be pivoted to course-correct adoption.
                </p>
                <p className="text-gray-600">
                  Use templates or easily build your surveys to sense-check your stakeholders.
                </p>
                <a href="#contact" className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold hover:from-orange-600 hover:to-pink-600 transition-all duration-200">
                  Get a Demo
                  <FaArrowRight className="ml-2" /></a>
              </div>
              <div className="relative">
                <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-200">
                  <img
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80"
                    alt="Stakeholder Dashboard"
                    className="w-full rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* AI Data Questions */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="lg:order-2 space-y-6">
                <h3 className="text-3xl lg:text-4xl font-bold text-gray-900">
                  Get your data questions answered in real time
                </h3>
                <p className="text-gray-600">
                  Ask using natural language to get data-backed answers. Whether you want to pull the right data or do analysis use AI to save time.
                </p>
                <p className="text-gray-600">
                  Answer the toughest questions from your stakeholders immediately.
                </p>
                <p className="text-gray-600">
                  Save time and analyst resources.
                </p>
                <a href="#contact" className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold hover:from-orange-600 hover:to-pink-600 transition-all duration-200">
                  Get a Demo
                  <FaArrowRight className="ml-2" /></a>
                </div>
              <div className="lg:order-1 relative">
                <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-200">
                  <img
                    src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80"
                    alt="AI Analytics Dashboard"
                    className="w-full rounded-lg"
                  />
                </div>
              </div>
            </div>
            
            {/* Change Saturation */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h3 className="text-3xl lg:text-4xl font-bold text-gray-900">
                  Address change saturation risks
                </h3>
                <p className="text-gray-600">
                  Use ready-made charts to view change saturation hotspots. Drill into the data to find out why and for whom.
                </p>
                <p className="text-gray-600">
                  Get system recommendations on how to better sequence and prioritise initiatives to protect business performance. Show your stakeholders recommended options.
                </p>
                <a href="#contact" className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold hover:from-orange-600 hover:to-pink-600 transition-all duration-200">
                  Get a Demo
                  <FaArrowRight className="ml-2" /></a>
              </div>
            <div className="relative">
                <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-200">
                  <img
                    src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80"
                    alt="Change Saturation Analysis"
                    className="w-full rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Logos */}
      <section id="customers" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">A few of our Customers</h2>
            <h3 className="text-3xl lg:text-4xl font-bold gradient-text mb-12">
              These brands trust us as the partner in their change journey
            </h3>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center justify-items-center opacity-60">
            {customerLogos.map((customer, index) => (
              <div key={index} className="w-24 h-16 flex items-center justify-center">
                <img 
                  src={customer.logo} 
                  alt={customer.name}
                  className="max-w-full max-h-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                />
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a href="#contact" className="inline-flex items-center px-8 py-4 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-xl">
              Get a Demo
              <FaArrowRight className="ml-2" /></a>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Customer Testimonials
            </h2>
          </div>
          
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card p-8 rounded-2xl shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400 text-lg" />
                  ))}
                </div>
                <blockquote className="text-gray-700 mb-6 italic leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                <div className="border-t pt-4">
                  <div className="font-semibold text-gray-900">{testimonial.author}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                  <div className="text-sm font-medium text-blue-600">{testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 bg-gradient-to-b from-white to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Designed for enterprise level security
            </h2>
            
            <div className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-8 py-4 rounded-lg inline-block hover:from-orange-600 hover:to-pink-600 transition-all duration-200 cursor-pointer">
              <span className="font-semibold">Click Here</span>
            </div>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We have passed the most rigorous technology security and quality standards.
            </p>
            
            <div className="flex flex-col md:flex-row gap-8 justify-center items-center">
              <div className="flex items-center space-x-4 p-6 bg-orange-50 rounded-lg border border-orange-100">
                <FaShieldAlt className="text-3xl text-emerald-500" />
                <div>
                  <div className="font-bold text-gray-900">ISO27001</div>
                  <div className="text-sm text-gray-600">Certified</div>
                </div>
              </div>
              <div className="flex items-center space-x-4 p-6 bg-orange-50 rounded-lg border border-orange-100">
                <FaShieldAlt className="text-3xl text-coral-500" />
                <div>
                  <div className="font-bold text-gray-900">SOC-2</div>
                  <div className="text-sm text-gray-600">Compliant</div>
                </div>
              </div>
            </div>
            
            <p className="text-gray-600">
              Questions? Check out our FAQs for answers.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="demo-section py-20 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-8">
            Ready to take your company's transformation to the next level?
          </h2>
          
          <a href="#contact" className="inline-flex items-center px-8 py-4 rounded-lg bg-white text-gray-900 font-semibold text-lg hover:bg-gray-100 transition-all duration-200 shadow-xl">
            Book Now
            <FaArrowRight className="ml-2" /></a>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Choose Your Plan
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Flexible pricing options designed to scale with your organization's change management needs.
            </p>
          </div>
          
          {pricingLoading ? (
            <div className="flex justify-center items-center py-20">
              <FaSpinner className="animate-spin text-4xl text-orange-500" />
              <span className="ml-4 text-xl text-gray-600">Loading pricing plans...</span>
            </div>
          ) : pricingError ? (
            <div className="text-center py-20">
              <p className="text-red-600 mb-4">{pricingError}</p>
              <p className="text-gray-600">Please try refreshing the page or contact support.</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {pricingPlans.map((plan, index) => (
              <div key={index} className={`relative p-6 rounded-2xl border-2 transition-all duration-300 hover:shadow-lg ${
                plan.popular 
                    ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 transform scale-105' 
                  : 'border-gray-200 bg-white hover:border-blue-300'
              }`}>
                {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                      {plan.period && <span className="text-gray-600">/{plan.period}</span>}
                  </div>
                    <p className="text-gray-600 mb-6">{plan.description}</p>
                
                    <ul className="space-y-3 mb-8 text-left">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                          <FaCheck className="text-emerald-500 mr-3 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                    <a href="/register" className={`w-full inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  plan.popular
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:from-orange-600 hover:to-pink-600 shadow-lg hover:shadow-xl'
                        : 'bg-orange-100 text-gray-900 hover:bg-orange-200'
                }`}>
                      {plan.ctaText || 'Get Started'}</a>
                  </div>
              </div>
            ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-white to-orange-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Get in Touch
            </h2>
            <p className="text-xl text-gray-600">
              Ready to transform your change management approach? Let's talk about your needs.
            </p>
          </div>
              
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => handleContactChange('name', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 transition-all duration-200"
                    />
                  </div>
                  <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => handleContactChange('email', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 transition-all duration-200"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Company</label>
                    <input
                      type="text"
                      value={contactForm.company}
                      onChange={(e) => handleContactChange('company', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => handleContactChange('phone', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 transition-all duration-200"
                    />
                  </div>
                </div>
                
                <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                  <textarea
                rows={6}
                    required
                    value={contactForm.message}
                    onChange={(e) => handleContactChange('message', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                placeholder="Tell us about your change management challenges..."
                  />
                </div>
                
            <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                className="inline-flex items-center px-8 py-4 rounded-lg bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold text-lg hover:from-orange-600 hover:to-pink-600 transition-all duration-200 disabled:opacity-50 shadow-xl hover:shadow-2xl"
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <FaArrowRight className="ml-2" />
                  </>
                )}
                </button>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-orange-900 to-pink-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <img src={logo} alt="ezOCM Change Management Platform" className="h-24 w-auto" />
              <p className="text-orange-100">
                Enterprise change management platform for successful organizational transformation.
              </p>
              </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-orange-100">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><button type="button" className="hover:text-white transition-colors bg-transparent border-0 text-left cursor-pointer">Security</button></li>
                <li><button type="button" className="hover:text-white transition-colors bg-transparent border-0 text-left cursor-pointer">Integrations</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-orange-100">
                <li><button type="button" className="hover:text-white transition-colors bg-transparent border-0 text-left cursor-pointer">Documentation</button></li>
                <li><button type="button" className="hover:text-white transition-colors bg-transparent border-0 text-left cursor-pointer">API Reference</button></li>
                <li><button type="button" className="hover:text-white transition-colors bg-transparent border-0 text-left cursor-pointer">Case Studies</button></li>
                <li><button type="button" className="hover:text-white transition-colors bg-transparent border-0 text-left cursor-pointer">Support</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-orange-100">
                <li><button type="button" className="hover:text-white transition-colors bg-transparent border-0 text-left cursor-pointer">About</button></li>
                <li><button type="button" className="hover:text-white transition-colors bg-transparent border-0 text-left cursor-pointer">Careers</button></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
                <li><button type="button" className="hover:text-white transition-colors bg-transparent border-0 text-left cursor-pointer">Privacy</button></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2024 Change Management Platform. All rights reserved.
            </p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <button type="button" className="text-gray-400 hover:text-white transition-colors bg-transparent border-0 cursor-pointer">
                <FaLinkedin className="text-xl" />
              </button>
              <button type="button" className="text-gray-400 hover:text-white transition-colors bg-transparent border-0 cursor-pointer">
                <FaTwitter className="text-xl" />
              </button>
              <button type="button" className="text-gray-400 hover:text-white transition-colors bg-transparent border-0 cursor-pointer">
                <FaFacebook className="text-xl" />
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
} 






